import React, { useState, useEffect, useCallback } from "react";
import { JsonRpc } from "@proton/js";
import Loader from "./loader/Loader";

interface PlatformIncomeProps {
  session: any;
}

interface V3Config {
  admin: string;
  stake_token_contract: string;
  stake_token_symbol: string;
  profit_token_contract: string;
  profit_token_symbol: string;
  prediction_contract: string;
  lock_duration: number;
  min_stake_age_sec: number;
  total_staked: number;
  total_stakers: number;
  paused: boolean;
}

interface StakePosition {
  id: number;
  owner: string;
  staked_amount: number;
  staked_at: number;
}

interface StakerInfo {
  account: string;
  total_staked: number;
  stake_count: number;
  last_staked_at: number;
  last_updated: number;
}

interface ProfitBalance {
  user: string;
  balance: string;
}

interface LastDist {
  last_distribute_at: number;
}

/**
 * Parse a symbol string like "6,XPRED" into { precision, name }.
 */
function parseSymbol(sym: string): { precision: number; name: string } {
  if (typeof sym === "string" && sym.includes(",")) {
    const parts = sym.split(",");
    return { precision: parseInt(parts[0], 10), name: parts[1] };
  }
  return { precision: 4, name: sym || "XPRED" };
}

/**
 * Format a raw integer amount using the token precision.
 */
function formatAmount(raw: number, precision: number): string {
  const divisor = Math.pow(10, precision);
  return (raw / divisor).toFixed(precision);
}

/**
 * Format seconds into a human-readable duration.
 */
function formatDuration(seconds: number): string {
  if (seconds <= 0) return "No lock";
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

/**
 * Format a Unix timestamp to a locale date string.
 */
function formatDate(timestamp: number): string {
  if (timestamp <= 0) return "N/A";
  return new Date(timestamp * 1000).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Get time remaining until lock expiry.
 */
function getTimeRemaining(stakedAt: number, lockDuration: number): string {
  const now = Math.floor(Date.now() / 1000);
  const expiresAt = stakedAt + lockDuration;
  const remaining = expiresAt - now;
  if (remaining <= 0) return "Unlocked";
  return formatDuration(remaining) + " left";
}

const PlatformIncome: React.FC<PlatformIncomeProps> = ({ session }) => {
  // Contract config
  const profitV3Contract =
    process.env.REACT_APP_PROFITV3_CONTRACT || "xpredadmin";
  const rpcEndpoint =
    process.env.REACT_APP_RPC_ENDPOINT ||
    process.env.REACT_APP_PROTON_ENDPOINT ||
    "https://proton.eosusa.io";

  // State
  const [config, setConfig] = useState<V3Config | null>(null);
  const [stakes, setStakes] = useState<StakePosition[]>([]);
  const [stakerInfo, setStakerInfo] = useState<StakerInfo | null>(null);
  const [profitBalance, setProfitBalance] = useState<ProfitBalance | null>(
    null,
  );
  const [lastDist, setLastDist] = useState<LastDist | null>(null);
  const [loading, setLoading] = useState(true);
  const [stakeAmount, setStakeAmount] = useState("");
  const [stakeLoading, setStakeLoading] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);
  const [unstakeLoading, setUnstakeLoading] = useState<number | null>(null);
  const [walletBalance, setWalletBalance] = useState<string>("0");
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);

  const showToast = (
    message: string,
    type: "success" | "error" = "success",
  ) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const accountName = session?.auth?.actor?.toString() || "";

  // Fetch all data from blockchain
  const fetchData = useCallback(async () => {
    if (!session) return;

    try {
      const rpc = new JsonRpc(rpcEndpoint);

      // Fetch config singleton
      const configResult = await rpc.get_table_rows({
        json: true,
        code: profitV3Contract,
        scope: profitV3Contract,
        table: "config",
        limit: 1,
      });

      let configData: V3Config | null = null;
      if (configResult.rows && configResult.rows.length > 0) {
        configData = configResult.rows[0] as V3Config;
        setConfig(configData);
      }

      // Fetch user's stake positions
      const stakesResult = await rpc.get_table_rows({
        json: true,
        code: profitV3Contract,
        scope: profitV3Contract,
        table: "stakes",
        limit: 100,
      });

      if (stakesResult.rows) {
        const userStakes = stakesResult.rows.filter(
          (row: StakePosition) => row.owner === accountName,
        );
        setStakes(userStakes);
      }

      // Fetch user's staker aggregate record
      const stakerResult = await rpc.get_table_rows({
        json: true,
        code: profitV3Contract,
        scope: profitV3Contract,
        table: "stakers",
        lower_bound: accountName,
        upper_bound: accountName,
        limit: 1,
      });

      if (stakerResult.rows && stakerResult.rows.length > 0) {
        setStakerInfo(stakerResult.rows[0] as StakerInfo);
      } else {
        setStakerInfo(null);
      }

      // Fetch user's profit balance
      const balResult = await rpc.get_table_rows({
        json: true,
        code: profitV3Contract,
        scope: profitV3Contract,
        table: "profitbal",
        lower_bound: accountName,
        upper_bound: accountName,
        limit: 1,
      });

      if (balResult.rows && balResult.rows.length > 0) {
        setProfitBalance(balResult.rows[0] as ProfitBalance);
      } else {
        setProfitBalance(null);
      }

      // Fetch last distribution timestamp
      const lastDistResult = await rpc.get_table_rows({
        json: true,
        code: profitV3Contract,
        scope: profitV3Contract,
        table: "lastdist",
        limit: 1,
      });

      if (lastDistResult.rows && lastDistResult.rows.length > 0) {
        setLastDist(lastDistResult.rows[0] as LastDist);
      } else {
        setLastDist(null);
      }

      // Fetch user's wallet balance for the stake token
      if (configData) {
        const { name: tokenName } = parseSymbol(configData.stake_token_symbol);
        const walletResult = await rpc.get_currency_balance(
          configData.stake_token_contract,
          accountName,
          tokenName,
        );
        if (walletResult && walletResult.length > 0) {
          const balStr = walletResult[0].split(" ")[0];
          setWalletBalance(balStr);
        } else {
          setWalletBalance("0");
        }
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching platform income data:", error);
      setLoading(false);
    }
  }, [session, profitV3Contract, rpcEndpoint, accountName]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Stake tokens
  const handleStake = async () => {
    if (!session || !config) return;

    const amount = parseFloat(stakeAmount);
    if (isNaN(amount) || amount <= 0) {
      showToast("Please enter a valid amount", "error");
      return;
    }

    const { precision, name: tokenName } = parseSymbol(
      config.stake_token_symbol,
    );
    const formattedAmount = amount.toFixed(precision);

    setStakeLoading(true);
    try {
      await session.transact({
        actions: [
          {
            account: config.stake_token_contract,
            name: "transfer",
            authorization: [
              {
                actor: session.auth.actor,
                permission: session.auth.permission,
              },
            ],
            data: {
              from: session.auth.actor.toString(),
              to: profitV3Contract,
              quantity: `${formattedAmount} ${tokenName}`,
              memo: "stake",
            },
          },
        ],
      });

      showToast(`Successfully staked ${formattedAmount} ${tokenName}!`);
      setStakeAmount("");
      fetchData();
    } catch (error) {
      console.error("Error staking:", error);
      showToast("Failed to stake: " + error, "error");
    } finally {
      setStakeLoading(false);
    }
  };

  // Unstake a specific position
  const handleUnstake = async (stakeId: number) => {
    if (!session || !config) return;

    const now = Math.floor(Date.now() / 1000);
    const stake = stakes.find((s) => s.id === stakeId);
    if (stake && stake.staked_at + config.lock_duration > now) {
      showToast(
        "This stake is still locked. Please wait for the lock period to expire.",
        "error",
      );
      return;
    }

    setUnstakeLoading(stakeId);
    try {
      await session.transact({
        actions: [
          {
            account: profitV3Contract,
            name: "unstake",
            authorization: [
              {
                actor: session.auth.actor,
                permission: session.auth.permission,
              },
            ],
            data: {
              owner: session.auth.actor.toString(),
              stake_id: stakeId,
            },
          },
        ],
      });

      showToast("Successfully unstaked!");
      fetchData();
    } catch (error) {
      console.error("Error unstaking:", error);
      showToast("Failed to unstake: " + error, "error");
    } finally {
      setUnstakeLoading(null);
    }
  };

  // Claim accumulated profit
  const handleClaimProfit = async () => {
    if (!session) return;

    setClaimLoading(true);
    try {
      await session.transact({
        actions: [
          {
            account: profitV3Contract,
            name: "claimprofit",
            authorization: [
              {
                actor: session.auth.actor,
                permission: session.auth.permission,
              },
            ],
            data: {
              user: session.auth.actor.toString(),
            },
          },
        ],
      });

      showToast("Profit claimed successfully!");
      fetchData();
    } catch (error) {
      console.error("Error claiming profit:", error);
      showToast("Failed to claim profit: " + error, "error");
    } finally {
      setClaimLoading(false);
    }
  };

  // Set max stake amount
  const handleMaxStake = () => {
    setStakeAmount(walletBalance);
  };

  // Parse derived values
  const stakeSymbol = config
    ? parseSymbol(config.stake_token_symbol)
    : { precision: 4, name: "XPRED" };
  const profitSymbol = config
    ? parseSymbol(config.profit_token_symbol)
    : { precision: 6, name: "XUSDC" };

  const totalStakedFormatted = config
    ? formatAmount(config.total_staked, stakeSymbol.precision)
    : "0";
  const userTotalStaked = stakerInfo
    ? formatAmount(stakerInfo.total_staked, stakeSymbol.precision)
    : "0";
  const claimableBalance =
    profitBalance?.balance ||
    `0.${"0".repeat(profitSymbol.precision)} ${profitSymbol.name}`;
  const hasClaimable = profitBalance
    ? parseFloat(profitBalance.balance.split(" ")[0]) > 0
    : false;

  if (loading) {
    return <Loader label="Loading platform income..." />;
  }

  if (!config) {
    return (
      <div className="platform-income">
        <h2>Platform Income</h2>
        <div className="pi-empty-state">
          <p>
            Platform income contract is not yet configured. Please check back
            later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="platform-income">
      {/* Toast notification */}
      {toast && <div className={`pi-toast ${toast.type}`}>{toast.message}</div>}

      <h2>Platform Income</h2>
      <p className="pi-subtitle">
        Stake your <strong>{stakeSymbol.name}</strong> tokens to earn a share of
        platform revenue distributed weekly.
      </p>

      {config.paused && (
        <div className="pi-paused-banner">
          Contract is currently paused. Staking and claiming are temporarily
          disabled.
        </div>
      )}

      {/* Overview Stats */}
      <div className="pi-stats-grid">
        <div className="pi-stat-card">
          <span className="pi-stat-label">Total Staked</span>
          <span className="pi-stat-value">{totalStakedFormatted}</span>
          <span className="pi-stat-token">{stakeSymbol.name}</span>
        </div>
        <div className="pi-stat-card">
          <span className="pi-stat-label">Total Stakers</span>
          <span className="pi-stat-value">{config.total_stakers}</span>
          <span className="pi-stat-token">users</span>
        </div>
        <div className="pi-stat-card highlight">
          <span className="pi-stat-label">Your Staked</span>
          <span className="pi-stat-value">{userTotalStaked}</span>
          <span className="pi-stat-token">{stakeSymbol.name}</span>
        </div>
        <div className="pi-stat-card highlight green">
          <span className="pi-stat-label">Claimable Profit</span>
          <span className="pi-stat-value claimable">{claimableBalance}</span>
        </div>
      </div>

      {/* Two column layout: Stake Form + Claim Section */}
      <div className="pi-actions-row">
        {/* Stake Form */}
        <div className="pi-section pi-stake-form">
          <h3>Stake {stakeSymbol.name}</h3>
          <p className="pi-section-desc">
            Stake your tokens to earn platform income. Tokens are locked for{" "}
            <strong>{formatDuration(config.lock_duration)}</strong> after
            staking.
          </p>
          <div className="pi-wallet-balance">
            Wallet Balance:{" "}
            <strong>
              {walletBalance} {stakeSymbol.name}
            </strong>
          </div>
          <div className="pi-input-group">
            <input
              type="number"
              value={stakeAmount}
              onChange={(e) => setStakeAmount(e.target.value)}
              placeholder={`Amount of ${stakeSymbol.name} to stake`}
              min="0"
              step="any"
              className="pi-input"
              disabled={config.paused || stakeLoading}
            />
            <button
              className="pi-max-btn"
              onClick={handleMaxStake}
              disabled={config.paused || stakeLoading}
            >
              MAX
            </button>
          </div>
          <button
            className="pi-stake-btn"
            onClick={handleStake}
            disabled={config.paused || stakeLoading || !stakeAmount}
          >
            {stakeLoading ? "Staking..." : `Stake ${stakeSymbol.name}`}
          </button>
        </div>

        {/* Claim Section */}
        <div className="pi-section pi-claim-section">
          <h3>Claim Profit</h3>
          <p className="pi-section-desc">
            Your accumulated platform income from staking rewards, distributed
            weekly.
          </p>
          <div className="pi-claim-display">
            <span className="pi-claim-label">Available to Claim</span>
            <span className="pi-claim-amount">{claimableBalance}</span>
          </div>
          <button
            className="pi-claim-btn"
            onClick={handleClaimProfit}
            disabled={config.paused || claimLoading || !hasClaimable}
          >
            {claimLoading ? "Claiming..." : "Claim Profit"}
          </button>
          <div className="pi-claim-info">
            <div className="pi-info-row">
              <span>Profit Token:</span>
              <strong>{profitSymbol.name}</strong>
            </div>
            <div className="pi-info-row">
              <span>Last Distribution:</span>
              <strong>
                {lastDist
                  ? formatDate(lastDist.last_distribute_at)
                  : "None yet"}
              </strong>
            </div>
            <div className="pi-info-row">
              <span>Min Stake Age:</span>
              <strong>{formatDuration(config.min_stake_age_sec)}</strong>
            </div>
          </div>
        </div>
      </div>

      {/* Staking Positions List */}
      <div className="pi-section pi-stakes-section">
        <h3>Your Staking Positions</h3>
        {stakes.length === 0 ? (
          <div className="pi-empty-state">
            <p>
              You have no active stakes. Stake your {stakeSymbol.name} tokens
              above to start earning platform income.
            </p>
          </div>
        ) : (
          <>
            <div className="pi-stakes-header">
              <span>ID</span>
              <span>Amount</span>
              <span>Staked Date</span>
              <span>Lock Status</span>
              <span>Action</span>
            </div>
            <div className="pi-stakes-list">
              {stakes.map((stake) => {
                const now = Math.floor(Date.now() / 1000);
                const isLocked = stake.staked_at + config.lock_duration > now;
                return (
                  <div key={stake.id} className="pi-stake-row">
                    <span className="pi-stake-id">#{stake.id}</span>
                    <span className="pi-stake-amount">
                      {formatAmount(stake.staked_amount, stakeSymbol.precision)}{" "}
                      {stakeSymbol.name}
                    </span>
                    <span className="pi-stake-date">
                      {formatDate(stake.staked_at)}
                    </span>
                    <span
                      className={`pi-stake-lock ${isLocked ? "locked" : "unlocked"}`}
                    >
                      {isLocked ? (
                        <>
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            width="14"
                            height="14"
                          >
                            <rect
                              x="3"
                              y="11"
                              width="18"
                              height="11"
                              rx="2"
                              ry="2"
                            />
                            <path d="M7 11V7a5 5 0 0110 0v4" />
                          </svg>
                          {getTimeRemaining(
                            stake.staked_at,
                            config.lock_duration,
                          )}
                        </>
                      ) : (
                        <>
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            width="14"
                            height="14"
                          >
                            <rect
                              x="3"
                              y="11"
                              width="18"
                              height="11"
                              rx="2"
                              ry="2"
                            />
                            <path d="M7 11V7a5 5 0 019.9-1" />
                          </svg>
                          Unlocked
                        </>
                      )}
                    </span>
                    <span className="pi-stake-action">
                      <button
                        className="pi-unstake-btn"
                        onClick={() => handleUnstake(stake.id)}
                        disabled={
                          isLocked ||
                          unstakeLoading === stake.id ||
                          config.paused
                        }
                        title={
                          isLocked
                            ? "Stake is still locked"
                            : "Unstake this position"
                        }
                      >
                        {unstakeLoading === stake.id
                          ? "Unstaking..."
                          : "Unstake"}
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* How it works */}
      <div className="pi-section pi-how-it-works">
        <h3>How Platform Income Works</h3>
        <div className="pi-steps">
          <div className="pi-step">
            <div className="pi-step-number">1</div>
            <div className="pi-step-content">
              <h4>Stake {stakeSymbol.name}</h4>
              <p>
                Stake your tokens in the platform income contract. Your tokens
                will be locked for {formatDuration(config.lock_duration)}.
              </p>
            </div>
          </div>
          <div className="pi-step">
            <div className="pi-step-number">2</div>
            <div className="pi-step-content">
              <h4>Earn Platform Revenue</h4>
              <p>
                Platform fees are collected and distributed proportionally to
                all stakers based on their stake weight.
              </p>
            </div>
          </div>
          <div className="pi-step">
            <div className="pi-step-number">3</div>
            <div className="pi-step-content">
              <h4>Claim Weekly</h4>
              <p>
                After distribution, claim your accumulated {profitSymbol.name}{" "}
                profit. Stakes must be at least{" "}
                {formatDuration(config.min_stake_age_sec)} old to be eligible.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformIncome;
